import * as Y from "yjs";
/**
 * CrdtSession — singleton coordinator voor de realtime-sync.
 *
 * Houdt één Y.Doc + één CrdtProvider per (workspaceId, branchId).
 * Bij een wisseling van branch wordt de oude opgeruimd en een nieuwe
 * opgezet.
 *
 * **Integratie met de bestaande store (M3 first cut):**
 *
 *   - Lokale mutaties gaan nog steeds via de bestaande optimistic-API-
 *     flow in `useWorkspaceData`. Dit is bewust: één bron van waarheid,
 *     geen risk dat we de store breaken tijdens M3.
 *   - Wanneer de provider een **remote** update ontvangt (vanuit een
 *     andere editor in dezelfde branch), triggeren we `hydrate()` zodat
 *     de store de nieuwe data uit D1 ophaalt. Dat is grof maar werkt:
 *     de DO heeft de Y.Doc vers gebootstrapt vanuit D1, dus elke
 *     mutation door peer-X die in D1 landt komt via de DO terug bij
 *     ons als signaal "er is iets veranderd, ververs."
 *
 * In M4 vervangen we dit door echte CRDT-driven state: lokale mutaties
 * editen het Y.Doc direct (via `setConcept` etc. uit `@ae/crdt`), en
 * de store *observeert* het Y.Doc. Dat gaat veel meer gelijktijdige
 * edits vlot afhandelen, maar vereist een grotere refactor van de
 * undo-stack en optimistic-rollback. Voor M3 is "merk en herlaad" een
 * acceptabel compromis dat al meerdere gebruikers tegelijk laat zien
 * wat er gebeurt.
 *
 * **Feature-flag:** kan uit-gezet worden via `localStorage.setItem(
 * "ae.crdt.disabled", "1")` zodat we bij problemen onmiddellijk
 * terug kunnen vallen op pure single-user-modus.
 */
import { useChatStore } from "../state/chat";
import { useSession } from "../state/session";
import type { AwarenessRecord, CrdtConnectionStatus } from "./CrdtProvider";
import { CrdtProvider, REMOTE_ORIGIN } from "./CrdtProvider";

const DISABLE_FLAG_KEY = "ae.crdt.disabled";

interface ActiveSession {
  workspaceId: string;
  branchId: string;
  doc: Y.Doc;
  provider: CrdtProvider;
  /** Debounce-timer zodat een rafale van peer-updates niet 50 hydrates triggert. */
  rehydrateTimer: ReturnType<typeof setTimeout> | null;
}

let active: ActiveSession | null = null;
const statusListeners: Set<(status: CrdtConnectionStatus) => void> = new Set();
const awarenessListeners: Set<(records: AwarenessRecord[]) => void> = new Set();
let lastStatus: CrdtConnectionStatus = "idle";
let lastAwareness: AwarenessRecord[] = [];
/** Eigen `connectionId` zodra de server 'm bevestigt — gebruikt om
 *  jezelf uit de PresenceBar te filteren. Null vóór de eerste sync. */
let myConnectionId: string | null = null;

export function getMyConnectionId(): string | null {
  return myConnectionId;
}

/** Het e-mailadres van de ingelogde gebruiker, of null zolang de sessie nog
 *  niet gehydrateerd is. Nodig om binnenkomende chat aan de juiste peer te
 *  hangen: in een bericht ben je nu eens afzender, dan weer ontvanger. */
function getMyEmail(): string | null {
  return useSession.getState().me?.email ?? null;
}

/** Stuur een bericht aan een co-editor in deze branch. Stil als er geen
 *  verbinding is — chat is een extraatje, geen kritiek pad. */
export function sendChatMessage(
  recipientEmail: string,
  text: string,
  diagramRef?: { id: string; name: string },
): void {
  active?.provider.sendChat(recipientEmail, text, diagramRef);
}

/** Meld dat we het gesprek met deze peer openen, zodat z'n envelopje weg kan. */
export function notifyChatOpened(peerEmail: string): void {
  active?.provider.sendChatOpened(peerEmail);
}
/** Callback gezet door `useWorkspaceData` om bij remote-updates de
 *  lokale store te herhydrateren. Optioneel — als 'ie niet gezet is
 *  doen we niets (handig voor tests / pre-init momenten). */
let rehydrateCallback: ((workspaceId: string, branchId: string) => void) | null = null;

const REHYDRATE_DEBOUNCE_MS = 250;

export function isCrdtDisabled(): boolean {
  try {
    return localStorage.getItem(DISABLE_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

export function setCrdtDisabled(disabled: boolean): void {
  try {
    if (disabled) localStorage.setItem(DISABLE_FLAG_KEY, "1");
    else localStorage.removeItem(DISABLE_FLAG_KEY);
  } catch {
    // ignore — privacy-mode etc.
  }
}

export function setRehydrateCallback(
  cb: ((workspaceId: string, branchId: string) => void) | null,
): void {
  rehydrateCallback = cb;
}

export function getCrdtStatus(): CrdtConnectionStatus {
  return lastStatus;
}

export function subscribeCrdtStatus(listener: (status: CrdtConnectionStatus) => void): () => void {
  statusListeners.add(listener);
  // Levert direct de huidige status zodat UI niet hoeft te wachten op
  // de eerstvolgende status-wissel.
  listener(lastStatus);
  return () => {
    statusListeners.delete(listener);
  };
}

function emitStatus(status: CrdtConnectionStatus): void {
  lastStatus = status;
  for (const l of statusListeners) {
    try {
      l(status);
    } catch (err) {
      console.error("[crdt-session] status listener threw", err);
    }
  }
}

export function subscribeAwareness(listener: (records: AwarenessRecord[]) => void): () => void {
  awarenessListeners.add(listener);
  listener(lastAwareness);
  return () => {
    awarenessListeners.delete(listener);
  };
}

function emitAwareness(records: AwarenessRecord[]): void {
  lastAwareness = records;
  for (const l of awarenessListeners) {
    try {
      l(records);
    } catch (err) {
      console.error("[crdt-session] awareness listener threw", err);
    }
  }
}

/** Stuur deze user's awareness-state naar de DO. */
export function setLocalAwareness(state: Record<string, unknown>): void {
  active?.provider.setAwarenessState(state);
}

/** Stuur een POKE — alle peers triggeren een rehydrate. Wordt
 *  aangeroepen vanuit de API-mutatie-hook. */
export function pokePeers(): void {
  active?.provider.poke();
}

/**
 * Alternatief voor `pokePeers()` dat het MSG_UPDATE-kanaal gebruikt
 * (bewezen werkend via initial-sync) in plaats van MSG_POKE. We
 * schrijven een teller in een Y.Map "mutation_ticks" — dat genereert
 * een Y-update met origin = lokaal → provider broadcast → andere
 * clients passen 'm toe → hun update-event met REMOTE_ORIGIN
 * triggert `scheduleRehydrate`. Bypassed elke MSG_POKE-bug aan
 * server-zijde.
 */
export function tickAfterMutation(): void {
  if (!active) return;
  const ticks = active.doc.getMap<number>("__mutation_ticks");
  active.doc.transact(() => {
    const next = (ticks.get("counter") ?? 0) + 1;
    ticks.set("counter", next);
    ticks.set("at", Date.now());
  }, "local-mutation");
}

/**
 * Open of switch de realtime-sessie naar de gegeven branch. Idempotent:
 * twee oproepen met dezelfde ids zijn een no-op.
 */
export function openCrdtSession(workspaceId: string, branchId: string): void {
  if (isCrdtDisabled()) return;
  if (active && active.workspaceId === workspaceId && active.branchId === branchId) {
    return;
  }
  closeCrdtSession();

  const doc = new Y.Doc();
  const provider = new CrdtProvider({
    workspaceId,
    branchId,
    doc,
    onStatusChange: emitStatus,
    onSynced: () => {
      // Initiële sync — in M3 first cut hoeven we niets te doen omdat
      // we toch al via de gewone API-hydrate de canonical data hebben.
      // Voor debug-doeleinden loggen we wel.
      console.info(`[crdt-session] synced with branch ${workspaceId}:${branchId}`);
    },
    onAwarenessChange: emitAwareness,
    onChat: (event) => {
      // De afzender is serverkant vastgesteld; wij bepalen alleen of het
      // binnenkomend is of onze eigen echo.
      const store = useChatStore.getState();
      if (event.op === "read-receipt") return;
      const myEmail = getMyEmail();
      if (!myEmail) return;
      store.ingest({ message: event.message, incoming: event.op === "received" }, myEmail);
    },
    onHello: (info) => {
      myConnectionId = info.connectionId;
    },
    onPoke: () => {
      // Een peer signaleerde een D1-mutatie — rehydrate (debounced).
      scheduleRehydrate();
    },
  });
  active = { workspaceId, branchId, doc, provider, rehydrateTimer: null };

  // Observeer doc-updates die NIET van onszelf komen (origin =
  // REMOTE_ORIGIN). Bij elke remote-update plannen we een (debounced)
  // rehydrate van de lokale store.
  doc.on("update", (_update: Uint8Array, origin: unknown) => {
    if (origin !== REMOTE_ORIGIN) return;
    scheduleRehydrate();
  });
}

export function closeCrdtSession(): void {
  if (!active) return;
  if (active.rehydrateTimer) {
    clearTimeout(active.rehydrateTimer);
  }
  active.provider.destroy();
  active.doc.destroy();
  active = null;
  myConnectionId = null;
  emitStatus("idle");
  emitAwareness([]);
  // Andere branch, ander gezelschap — gesprekken lopen daar niet doorheen.
  useChatStore.getState().reset();
}

function scheduleRehydrate(): void {
  if (!active) return;
  if (active.rehydrateTimer) clearTimeout(active.rehydrateTimer);
  active.rehydrateTimer = setTimeout(() => {
    if (!active) return;
    active.rehydrateTimer = null;
    if (rehydrateCallback) {
      try {
        rehydrateCallback(active.workspaceId, active.branchId);
      } catch (err) {
        console.error("[crdt-session] rehydrate callback threw", err);
      }
    }
  }, REHYDRATE_DEBOUNCE_MS);
}

/** Lees-handle op de actieve doc — voor tests / debug. */
export function getActiveCrdtDoc(): Y.Doc | null {
  return active?.doc ?? null;
}
